import { createSelector } from "@reduxjs/toolkit";
import { queryStringToObject } from "../../../../helpers/queryStringToObject";
import { decode, encode, PRICE_FACET_ID } from "../codec/urlCodec";
import {
  areSelectionsEqual,
  getSelectedRange,
  getSelectedValues,
} from "../model/selection";
import { COLOUR_FACET_ID } from "../../facet/constants";
import { isFacetPriceRange, isPriceNarrowed } from "../model/priceBounds";

export {
  default as getAllAppliedRefinements,
  getQuery,
} from "./getAllAppliedRefinements";

const emptyArray = [];
const emptyObject = {};
const defaultPriceBounds = { min: 0, max: 0, step: 5 };

const FACET_QUERY_KEYS = ["refine", "currentpricerange", "sort"];

const getRouterQuery = (router) => {
  const location = router?.locationBeforeTransitions || router?.location;

  return (
    location?.query || queryStringToObject(location?.search) || emptyObject
  );
};

const selectRouteFacetParams = (state) =>
  JSON.stringify(
    FACET_QUERY_KEYS.map((key) => getRouterQuery(state.router)[key] ?? null),
  );

const selectDecodedRoute = createSelector(
  [selectRouteFacetParams],
  (serialized) =>
    decode(
      Object.fromEntries(
        FACET_QUERY_KEYS.map((key, index) => [
          key,
          JSON.parse(serialized)[index] ?? undefined,
        ]),
      ),
    ),
);

const selectFacetState = (state) => state.facets || emptyObject;

export const selectPriceBounds = (state) =>
  selectFacetState(state).priceBounds || defaultPriceBounds;

export const selectFacetApplied = (state) =>
  selectDecodedRoute(state).selection;

const selectRouteBounds = (state) => selectDecodedRoute(state).bounds;

export const selectFacetDraft = (state) =>
  selectFacetState(state).draft ?? selectFacetApplied(state);

/**
 * Bounds already in the URL win over the API's, because once results are
 * filtered the API only reports the prices that survived the filter.
 */
const selectBoundsForQuery = (state) => {
  const boundsInQuery = selectRouteBounds(state);

  if (boundsInQuery) {
    return boundsInQuery;
  }

  const { min, max } = selectPriceBounds(state);

  return Number.isFinite(min) && Number.isFinite(max) && max > min
    ? { min, max }
    : undefined;
};

const selectFacetCatalog = (state) =>
  selectFacetState(state).catalog || emptyArray;

const selectKnownFacetValues = (state) =>
  selectFacetState(state).knownFacetValues || emptyObject;

const selectCurrencySymbol = (state) => state.config?.currency?.symbol || "";

const normalizeId = (id) => String(id);

const idsMatch = (left, right) => normalizeId(left) === normalizeId(right);

const getFacetValue = (facet, valueId) =>
  (facet.facetValues || []).find((facetValue) =>
    idsMatch(facetValue.id, valueId),
  );

const hasNonPriceSelection = (selection) =>
  Object.keys(selection?.facets || {}).some(
    (facetId) => !idsMatch(facetId, PRICE_FACET_ID),
  );

const formatPriceChipName = (currencySymbol, { min, max }) =>
  `${currencySymbol}${min} - ${currencySymbol}${max}`;

/**
 * A response can omit a value that is still selected. Falling back to the
 * remembered value keeps it visible, and therefore removable, at a count of 0.
 */
const resolveFacetValue = (facet, knownFacetValues, facetId, valueId) =>
  getFacetValue(facet, valueId) ||
  knownFacetValues[normalizeId(facetId)]?.[normalizeId(valueId)];

export const selectVisibleFacets = createSelector(
  [
    selectFacetCatalog,
    selectFacetDraft,
    selectPriceBounds,
    selectKnownFacetValues,
  ],
  (catalog, draft, bounds, knownFacetValues) =>
    catalog.map((facet) => {
      const selectedValueIds = new Set(
        getSelectedValues(draft, facet.id).map(normalizeId),
      );
      const reportedValues = facet.facetValues || [];
      const reportedValueIds = new Set(
        reportedValues.map(({ id }) => normalizeId(id)),
      );
      const missingSelectedValues = Array.from(selectedValueIds)
        .filter((valueId) => !reportedValueIds.has(valueId))
        .map((valueId) =>
          resolveFacetValue(facet, knownFacetValues, facet.id, valueId),
        )
        .filter(Boolean);
      const facetValues = reportedValues
        .concat(missingSelectedValues)
        .map((facetValue) => ({
          ...facetValue,
          isSelected: selectedValueIds.has(normalizeId(facetValue.id)),
        }))
        .filter((facetValue) => facetValue.count > 0 || facetValue.isSelected);
      const hasSelectedValues = idsMatch(facet.id, PRICE_FACET_ID)
        ? isPriceNarrowed(getSelectedRange(draft, PRICE_FACET_ID), bounds)
        : facetValues.some((facetValue) => facetValue.isSelected);

      const nextFacet = {
        ...facet,
        facetValues,
      };

      return facet.hasSelectedValues !== undefined || hasSelectedValues
        ? {
            ...nextFacet,
            hasSelectedValues,
          }
        : nextFacet;
    }),
);

export { isFacetPriceRange };

export const selectDraftPrice = (state) =>
  getSelectedRange(selectFacetDraft(state), PRICE_FACET_ID);

export const selectAppliedPrice = (state) =>
  getSelectedRange(selectFacetApplied(state), PRICE_FACET_ID);

export const getPriceSliderState = (state) => {
  const { min, max, step } = selectPriceBounds(state);
  const draftPrice = selectDraftPrice(state);
  const value = draftPrice
    ? { min: draftPrice.min, max: draftPrice.max }
    : { min, max };

  return { minValue: min, maxValue: max, step, value };
};

const getPriceRangeValues = (value, minValue, maxValue) => {
  const hasSelectedMin = value?.min > minValue;
  const hasSelectedMax = value?.max < maxValue;

  if (!hasSelectedMin && !hasSelectedMax) {
    return null;
  }

  return {
    min: hasSelectedMin ? value.min : minValue,
    max: hasSelectedMax ? value.max : maxValue,
  };
};

export const getSelectedPriceRangeRefinement = (state) => {
  const selectedPriceRange = getPriceSliderState(state);
  const currencySymbol = selectCurrencySymbol(state);

  const { value, minValue, maxValue } = selectedPriceRange || {};
  const priceValues = getPriceRangeValues(value, minValue, maxValue);

  if (!priceValues) {
    return null;
  }

  return {
    id: "price",
    name: formatPriceChipName(currencySymbol, priceValues),
  };
};

const priceToIndicator = (price) =>
  price ? { leftIndicator: price.min, rightIndicator: price.max } : undefined;

export const getNextPriceRange = (state) =>
  priceToIndicator(selectDraftPrice(state) || selectAppliedPrice(state));

export const getPrevPriceRange = (state) =>
  priceToIndicator(selectAppliedPrice(state));

export const hasColourFacet = createSelector([selectVisibleFacets], (facets) =>
  facets.some(({ id }) => idsMatch(id, COLOUR_FACET_ID)),
);

export const selectAppliedFacetChips = createSelector(
  [
    selectFacetApplied,
    selectFacetCatalog,
    selectCurrencySymbol,
    selectPriceBounds,
    selectKnownFacetValues,
  ],
  (applied, catalog, currencySymbol, bounds, knownFacetValues) => {
    const valueChips = Object.keys(applied.facets || {}).flatMap((facetId) => {
      if (idsMatch(facetId, PRICE_FACET_ID)) {
        return [];
      }

      const facet = catalog.find(({ id }) => idsMatch(id, facetId)) || {};

      return getSelectedValues(applied, facetId)
        .map((valueId) => {
          const facetValue = resolveFacetValue(
            facet,
            knownFacetValues,
            facetId,
            valueId,
          );

          return facetValue
            ? {
                facetId,
                id: valueId,
                name: facetValue.name,
              }
            : null;
        })
        .filter(Boolean);
    });
    const appliedPrice = getSelectedRange(applied, PRICE_FACET_ID);
    const priceChip = isPriceNarrowed(appliedPrice, bounds)
      ? [
          {
            facetId: PRICE_FACET_ID,
            id: "price",
            name: formatPriceChipName(currencySymbol, appliedPrice),
          },
        ]
      : [];

    return valueChips.concat(priceChip);
  },
);

export const selectHasPendingFacetChanges = (state) =>
  selectFacetState(state).draft != null &&
  !areSelectionsEqual(selectFacetApplied(state), selectFacetDraft(state));

export const selectFacetQuery = createSelector(
  [
    selectFacetDraft,
    (state) => getRouterQuery(state.router),
    selectBoundsForQuery,
  ],
  (draft, query, bounds) => ({
    ...query,
    ...encode(draft, bounds),
    page: undefined,
  }),
);

export const hasFacets = (state) => selectFacetCatalog(state).length > 0;

export const hasSelectedFacets = createSelector(
  [selectFacetApplied, selectFacetCatalog, selectPriceBounds],
  (applied, catalog, bounds) =>
    Boolean(
      hasNonPriceSelection(applied) ||
      isPriceNarrowed(getSelectedRange(applied, PRICE_FACET_ID), bounds) ||
      catalog.some(
        ({ id, hasSelectedValues }) =>
          hasSelectedValues && !idsMatch(id, PRICE_FACET_ID),
      ),
    ),
);

export const hasActiveFacetFilters = createSelector(
  [selectFacetApplied, selectFacetCatalog, selectPriceBounds],
  (applied, catalog, bounds) =>
    Boolean(
      hasNonPriceSelection(applied) ||
      isPriceNarrowed(getSelectedRange(applied, PRICE_FACET_ID), bounds) ||
      catalog.some(
        ({ id, hasSelectedValues }) =>
          idsMatch(id, PRICE_FACET_ID) && hasSelectedValues,
      ),
    ),
);

export const getFacetsWithSelections = createSelector(
  [selectVisibleFacets],
  (facets) => facets.filter((facet) => facet.hasSelectedValues),
);

export const getSort = (state) => {
  const canonicalSort = selectFacetApplied(state).sort;

  if (canonicalSort) {
    return canonicalSort;
  }

  return state.search?.query?.sort;
};

export const getSelectedValuesForFacet = createSelector(
  [selectVisibleFacets, (_state, facet) => facet],
  (facets, facet) => {
    const visibleFacet =
      facets.find(({ id }) => idsMatch(id, facet.id)) || facet;

    return (visibleFacet.facetValues || []).filter(
      (facetValue) => facetValue.isSelected,
    );
  },
);

export const getFacetSelectedValueNames = (facet) => {
  const { hasSelectedValues, facetValues = [] } = facet;

  return hasSelectedValues
    ? facetValues
        .filter(({ isSelected }) => isSelected)
        .map(({ name }) => name)
        .join(", ")
    : "";
};

export const getFacetSelectedCount = (facet) => {
  const { hasSelectedValues, facetValues = [] } = facet;

  return hasSelectedValues
    ? facetValues.filter(({ isSelected }) => isSelected).length
    : 0;
};
